import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CROWN_ENV_STORAGE_KEY,
  type CrownEnv,
  readPersistedEnv,
} from "./env-config";

type CrownEnvContextValue = {
  env: CrownEnv;
  setEnv: (env: CrownEnv) => void;
};

const CrownEnvContext = createContext<CrownEnvContextValue | null>(null);

type EnvProviderProps = {
  readonly children: ReactNode;
};

/**
 * Provides the currently selected Crown target environment to descendants.
 *
 * Mount this ONCE, above the router (see `index.tsx`). Two providers each seed
 * their own state from localStorage and cannot see each other's updates, while
 * Apollo re-reads localStorage per request, so a second mount lets the UI name
 * one env while requests go to another.
 *
 * Switching env reloads the page rather than only updating state. A state update
 * is not enough: the client's `no-cache` defaults are overridden by
 * `cache-first` at several call sites, so company, contact and service pickers
 * would keep serving the previous env's rows, and an operator could submit an
 * import against one env using another env's selections. Pages that do not read
 * the env would also keep rendering data fetched from the env just left. The
 * reload drops all of it, and because the cache is a plain `InMemoryCache` with
 * no persistence, reloading is what clears it — there is nothing to clear by
 * hand first.
 */
export const EnvProvider = ({ children }: EnvProviderProps) => {
  const [env, setEnv] = useState<CrownEnv>(() => readPersistedEnv());

  const selectEnv = useCallback((next: CrownEnv) => {
    localStorage.setItem(CROWN_ENV_STORAGE_KEY, next);
    // Reflect the choice before reloading, so the picker and the header chip show
    // the new env during the reload rather than appearing not to have responded.
    setEnv(next);
    globalThis.location.reload();
  }, []);

  // localStorage is shared across tabs of the same origin, and Apollo resolves
  // the endpoint from it per request. Without this, switching env in one tab
  // silently redirects every other tab's requests while those tabs still display
  // the old env. Reloading on the storage event keeps what a tab shows and what
  // it talks to in agreement.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === CROWN_ENV_STORAGE_KEY) {
        globalThis.location.reload();
      }
    };
    globalThis.addEventListener("storage", handleStorage);
    return () => {
      globalThis.removeEventListener("storage", handleStorage);
    };
  }, []);

  const value = useMemo<CrownEnvContextValue>(
    () => ({ env, setEnv: selectEnv }),
    [env, selectEnv],
  );

  return (
    <CrownEnvContext.Provider value={value}>
      {children}
    </CrownEnvContext.Provider>
  );
};

/**
 * Accesses the selected Crown target environment and its setter.
 *
 * @returns The current env, and a setter that persists the choice and reloads.
 * @throws Error when called outside of an `EnvProvider`.
 */
export const useCrownEnv = (): CrownEnvContextValue => {
  const context = useContext(CrownEnvContext);
  if (context === null) {
    throw new Error("useCrownEnv must be used within an EnvProvider");
  }
  return context;
};
